import { useMemo } from 'react';

import { useGetVideo } from '@vimeo/gctl-api/hooks/useVideo';
import type { VideoFields } from '@vimeo/gctl-api/models/components/schemas/video/Video';
import type { VideoFile } from '@vimeo/gctl-api/models/components/schemas/video-file/VideoFile';

import { filterVideoFilesForKnownResolutionThenSortAlphabetically } from '../utils';
import { mapVideoIdTypeForGCTL } from '../utils/mapVideoIdTypeForGCTL';

const CLIP_DOWNLOAD_FIELDS: VideoFields[] = [
  'download.height',
  'download.link',
  'download.quality',
  'download.rendition',
  'download.type',
  'download.width',
  'download.sizeShort',
  'download.publicName',
  'download.videoFileId',
];

type Props = {
  videoId?: string;
  disabled?: boolean;
  allowAudioDownload?: boolean;
};
export function useGetClipDownloadLinks({ videoId, disabled, allowAudioDownload }: Props) {
  const {
    error: videoFilesError,
    mutate: getVideoFiles,
    data: videoFilesResponse,
    isLoading,
    isValidating,
  } = useGetVideo(
    () => {
      if (!videoId || disabled) {
        return null;
      }
      return {
        where: mapVideoIdTypeForGCTL(videoId),
        select: CLIP_DOWNLOAD_FIELDS,
        headers: { Accept: 'application/vnd.vimeo.*+json;version=3.4.1' },
      };
    },
    {
      refreshWhenHidden: false,
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    },
  );

  const downloadableFiles: VideoFile[] = useMemo(() => {
    return filterVideoFilesForKnownResolutionThenSortAlphabetically(
      videoFilesResponse?.download ?? [],
      { allowAudioDownload },
    );
  }, [videoFilesResponse?.download, allowAudioDownload]);

  return {
    downloadableFiles,
    videoFilesError,
    getVideoFiles,
    isLoading,
    isValidating,
    isFetching: isLoading || isValidating,
  };
}
